import React from 'react';
import { CheckCircle, Circle, AlertCircle } from 'lucide-react';

/**
 * Progress Sidebar Component
 * Shows completion status of all sections with visual indicators
 */
const ProgressSidebar = ({
    sections,
    currentSection,
    completedSections,
    onSectionClick,
    validationErrors = {}
}) => {
    const getSectionStatus = (sectionId) => {
        if (completedSections.has(sectionId)) {
            return 'completed';
        }

        // Check if section has validation errors
        const sectionErrors = Object.keys(validationErrors).filter(key =>
            key.startsWith(`content.${sectionId}`)
        );

        if (sectionErrors.length > 0) {
            return 'error';
        }

        return 'pending';
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed':
                return <CheckCircle className="w-5 h-5 text-green-500" />;
            case 'error':
                return <AlertCircle className="w-5 h-5 text-red-500" />;
            default:
                return <Circle className="w-5 h-5 text-gray-300" />;
        }
    };

    const getStatusColor = (sectionId, status) => {
        if (currentSection === sectionId) {
            return 'bg-blue-50 border-blue-500 text-blue-700';
        }

        switch (status) {
            case 'completed':
                return 'bg-green-50 border-green-200 hover:border-green-400';
            case 'error':
                return 'bg-red-50 border-red-200 hover:border-red-400';
            default:
                return 'bg-white border-gray-200 hover:border-gray-400';
        }
    };

    // Group sections by category
    const groupedSections = sections.reduce((acc, section) => {
        const category = section.category || 'Other';
        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push(section);
        return acc;
    }, {});

    const completedCount = Array.from(completedSections).filter(id =>
        sections.some(s => s.id === id)
    ).length;
    const totalCount = sections.length;
    const progressPercentage = (completedCount / totalCount) * 100;

    return (
        <div className="sticky top-4 space-y-4">
            {/* Overall Progress */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Your Progress</h3>

                {/* Progress Bar */}
                <div className="mb-4">
                    <div className="flex justify-between text-sm text-gray-600 mb-2">
                        <span>{completedCount} of {totalCount} completed</span>
                        <span className="font-semibold text-blue-600">{Math.round(progressPercentage)}%</span>
                    </div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-500 ease-out"
                            style={{ width: `${progressPercentage}%` }}
                        />
                    </div>
                </div>

                {/* Encouragement Message */}
                {progressPercentage < 25 && (
                    <p className="text-xs text-gray-500 italic">
                        🚀 Great start! Keep going to build your amazing site.
                    </p>
                )}
                {progressPercentage >= 25 && progressPercentage < 50 && (
                    <p className="text-xs text-gray-500 italic">
                        💪 You're making great progress! Almost halfway there.
                    </p>
                )}
                {progressPercentage >= 50 && progressPercentage < 75 && (
                    <p className="text-xs text-gray-500 italic">
                        🎯 Over halfway! Your site is taking shape beautifully.
                    </p>
                )}
                {progressPercentage >= 75 && progressPercentage < 100 && (
                    <p className="text-xs text-gray-500 italic">
                        🌟 Almost there! Just a few more sections to complete.
                    </p>
                )}
                {progressPercentage === 100 && (
                    <p className="text-xs text-green-600 font-semibold italic">
                        ✨ Perfect! All sections completed. Ready to publish!
                    </p>
                )}
            </div>

            {/* Sections List */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 max-h-[calc(100vh-280px)] overflow-y-auto">
                {Object.entries(groupedSections).map(([category, categorySections]) => (
                    <div key={category} className="border-b border-gray-100 last:border-b-0">
                        <div className="px-4 py-3 bg-gray-50 sticky top-0 z-10">
                            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                                {category}
                            </h4>
                        </div>
                        <div className="p-2 space-y-1">
                            {categorySections.map((section) => {
                                const status = getSectionStatus(section.id);
                                const Icon = section.icon;

                                return (
                                    <button
                                        key={section.id}
                                        onClick={() => onSectionClick(section.id)}
                                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 transition-all duration-200 text-left ${getStatusColor(section.id, status)}`}
                                    >
                                        {getStatusIcon(status)}
                                        <Icon className="w-4 h-4 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium truncate">
                                                {section.name}
                                                {section.required && (
                                                    <span className="text-red-500 ml-1">*</span>
                                                )}
                                            </p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            {/* Auto-save Indicator */}
            <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                <div className="flex items-center gap-2 text-xs text-gray-600">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span>Auto-saving your progress</span>
                </div>
            </div>
        </div>
    );
};

export default ProgressSidebar;
